/**
 * The literal values a motif (AI) theme contributes to its wedding-website OG image.
 *
 * Extracted from the published theme stylesheet during SSR and handed to the edge image route as a
 * single JSON query param, which keeps that route free of CSS parsing and data fetching. This module
 * is dependency-free on purpose: it is imported by both the Node API and the edge bundle.
 */
export interface MotifOgTheme {
  surface: string;
  overlay: string;
  text: string;
  fontFamily?: string;
  fontWeight?: number;
}

export const serializeMotifOgTheme = (theme: MotifOgTheme): string => JSON.stringify(theme);

// The param is publicly reachable and satori only understands hex/rgb/hsl.
const COLOR =
  /^(#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\))$/i;
const FONT_FAMILY = /^[\w\s-]{1,60}$/;

export const parseMotifOgTheme = (raw: string | null): MotifOgTheme | null => {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const { surface, overlay, text, fontFamily, fontWeight } = parsed as Record<string, unknown>;
  const isColor = (value: unknown) => typeof value === 'string' && COLOR.test(value);
  if (!isColor(surface) || !isColor(overlay) || !isColor(text)) return null;

  const family = typeof fontFamily === 'string' && FONT_FAMILY.test(fontFamily) ? fontFamily : null;
  const weight =
    typeof fontWeight === 'number' && fontWeight >= 100 && fontWeight <= 900 ? fontWeight : null;

  return {
    surface: surface as string,
    overlay: overlay as string,
    text: text as string,
    ...(family ? { fontFamily: family } : {}),
    ...(weight ? { fontWeight: weight } : {}),
  };
};
